'use client';

const BASE_URL = 'https://www.inceptionai.ca';

/**
 * Organization Schema Component
 * Provides structured data for the Inception organization (legal entity
 * InceptionAI Inc. — kept as legalName + alternateName so the entity keeps
 * ranking on the "InceptionAI" / "Inception AI" branded keyword)
 * 2026 AEO-optimized: @id for entity graph linking, sameAs for Knowledge Graph disambiguation
 */
export const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}#organization`,
    name: 'Inception',
    legalName: 'InceptionAI Inc.',
    alternateName: [
      'InceptionAI',
      'Inception AI',
      'inception ai',
      'InceptionAi',
      'Inception Ai',
      'inception ia',
      'inceptionai',
    ],
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/images/inception-logo.jpg`,
      width: 200,
      height: 200,
    },
    description:
      'Inception (also known as InceptionAI, operated by InceptionAI Inc.) is an AI engine that creates and operates complete shopping experiences: trustworthy buying guides with verified facts, region-aware retail links, and a built-in AI advisor, kept accurate by a fleet of agents. It answers to you, not advertisers, and also powers standalone niche brands such as Brickonia.com.',
    foundingDate: '2023',
    sameAs: [
      'https://www.inceptionai.ca',
      'https://www.inceptionai.ca/en-CA/vision',
      'https://www.linkedin.com/company/inceptionai',
      'https://www.facebook.com/InceptionAI',
      'https://www.instagram.com/inceptionai_inc',
      'https://www.youtube.com/@InceptionAI',
      'https://github.com/inceptionai',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      availableLanguage: ['English', 'French', 'Spanish', 'German'],
      areaServed: ['CA', 'US'],
    },
    knowsAbout: [
      'Artificial Intelligence',
      'Product Discovery',
      'AI-Powered Search',
      'Product Recommendations',
      'E-commerce Technology',
      'Machine Learning',
      'Shopping Assistant',
      'Product Research',
      'Consumer Technology',
      'AI Search Tool API',
      'Agent-callable Product Discovery',
    ],
    areaServed: [
      {
        '@type': 'Country',
        name: 'Canada',
      },
      {
        '@type': 'Country',
        name: 'United States',
      },
    ],
    slogan: `The best product for you is already out there. Our AI finds it, because we answer to you, not advertisers.`,
    founder: {
      '@type': 'Person',
      '@id': `${BASE_URL}#founder`,
      name: 'Olivier Leclerc',
      jobTitle: 'Founder & CEO',
      worksFor: { '@id': `${BASE_URL}#organization` },
    },
    // The worlds built on the engine. Brickonia's own Organization node is
    // `https://brickonia.com/#organization` and declares this node as its
    // parentOrganization, so the two graphs reference each other by @id.
    // Atelier 700 joins this list the day atelier700.ca is live, not before:
    // a subOrganization whose url 404s is a claim search engines cannot verify.
    subOrganization: [
      {
        '@type': 'Organization',
        '@id': 'https://brickonia.com/#organization',
        name: 'Brickonia',
        legalName: 'InceptionAI Inc. dba Brickonia',
        url: 'https://brickonia.com/',
        parentOrganization: { '@id': `${BASE_URL}#organization` },
      },
    ],
    // No `award` field: schema.org `award` must name a real, third-party
    // honour. The placeholders that used to sit here ("Best AI Shopping
    // Assistant 2024/2025", "Top Product Discovery Platform") were not
    // conferred by anyone, so they were unverifiable self-claims shipped on
    // every page — the same class of problem as synthesizing aggregateRating.
    // Only re-add this with an award that has a citable source.
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema),
      }}
    />
  );
};

